/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { useCallback } from 'react';
import { FormFieldT, FormItemT } from './types';

// This hook encapsulates schema navigation logic.
// It takes the current formSchema so it always works with up-to-date data.
export function useSchemaNavigators(schema: FormItemT[]) {
  const determineActualParentId = useCallback(
    (itemPath: number[]): string | null => {
      if (!itemPath || itemPath.length <= 1) return null; // Item is at root or path is invalid

      const parentPath = itemPath.slice(0, -1);
      let currentLevelItems: FormItemT[] | undefined | null = schema;
      let parentItem: FormItemT | null = null;

      for (let i = 0; i < parentPath.length; /* eslint-disable-line */ i++) {
        const index = parentPath[i];
        if (!currentLevelItems || index < 0 || index >= currentLevelItems.length) {
          // console.warn("determineActualParentId: Invalid path segment", parentPath, i, index);
          return null;
        }

        const currentItem: FormItemT | null = currentLevelItems[index] || null;
        if (i === parentPath.length - 1) {
          // This segment points to the direct parent item
          parentItem = currentItem;
          break;
        }

        // Navigate deeper
        if ('fields' in currentItem && currentItem.fields) {
          currentLevelItems = currentItem.fields;
        } else if (
          'inputType' in currentItem &&
          currentItem?.inputType === 'yesNo' &&
          (currentItem as FormFieldT).followUpQuestionList
        ) {
          currentLevelItems = (currentItem as FormFieldT).followUpQuestionList as FormItemT[];
        } else {
          // console.warn("determineActualParentId: Path segment is not a container", currentItem);
          return null; // Parent path segment is not a container
        }
      }
      return parentItem ? parentItem.id : null;
    },
    [schema],
  ); // Dependency on schema ensures it uses the latest version

  const findFormItemById = useCallback(
    (id: string | null): FormItemT | null => {
      if (!id) return null;

      const findRecursive = (items: FormItemT[], targetId: string): FormItemT | null => {
        /* eslint-disable-next-line */
        for (const item of items) {
          if (item.id === targetId) return item;
          if ('fields' in item && item.fields) {
            const found = findRecursive(item.fields, targetId);
            if (found) return found;
          }
          const fieldSchemaItem = item as FormFieldT; // Type assertion
          if (fieldSchemaItem.followUpQuestionList && fieldSchemaItem.followUpQuestionList.length > 0) {
            const foundInFollowUp = findRecursive(fieldSchemaItem.followUpQuestionList as FormItemT[], targetId);
            if (foundInFollowUp) return foundInFollowUp;
          }
        }
        return null;
      };
      return findRecursive(schema, id);
    },
    [schema],
  ); // Dependency on schema

  return { determineActualParentId, findFormItemById };
}
// Remember to import useCallback from React at the top of this file.
